import { Can } from "@casl/react";
import TagValueInput from "@via/components/tag-value-input.tsx";
import { useAbility } from "@via/context/ability-context";
import { classNames } from "@via/utils/helpers";
import { ProjectViewType, TaggableEntityType } from "common";
import { useState } from "react";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";

type FilterItem = { id: string; value: unknown };

type ProjectsFilterProps = {
  columnFilters: FilterItem[];
  setColumnFilters: (
    filters: (prevFilters: FilterItem[]) => FilterItem[]
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  hideInputFilters?: boolean;
};

const selectedFilterClass =
  "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md";
const unSelectedFilterClass = "bg-white";
const leftButtonClass = "rounded-l-md min-w-[105px]";
const middleButtonClass = "-ml-px min-w-[135px]";
const rightButtonClass = "rounded-r-md -ml-px min-w-[105px]";
const defaultButtonClass =
  "relative inline-flex flex-1 justify-center h-9 items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10";

const ProjectsFilter = ({
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
  hideInputFilters = false,
}: ProjectsFilterProps) => {
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: false,
    },
  ]);

  const [selectedFilterType, setSelectedFilterType] = useState(
    columnFilters.find(filter => filter.id === "type")?.value ||
      ProjectViewType.MyProjects
  );

  const [debouncedNameFilter] = useDebounceValue(
    (columnFilters.find(filter => filter.id === "name")?.value as string) || "",
    300
  );

  const [debouncedOwnerFilter] = useDebounceValue(
    (columnFilters.find(filter => filter.id === "owner")?.value as string) ||
      "",
    300
  );

  const debounced = useDebounceCallback((id, value) => {
    setColumnFilters(prevFilters => {
      const updatedFilters = prevFilters.map(filter =>
        filter.id === id ? { ...filter, value } : filter
      );
      if (!prevFilters.find(filter => filter.id === id) && value !== "") {
        return [...updatedFilters, { id, value }];
      }
      return updatedFilters.filter(filter => filter.value !== "");
    });
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleSelectPipelineType = (type: number) => {
    setSelectedFilterType(type);
    debounced("type", type);
  };

  const currentTagFilter =
    (columnFilters.find(filter => filter.id === "tagIds")?.value as string) ||
    "";

  const handleTagFilterChange = (value: string) => {
    setColumnFilters(prevFilters => {
      const withoutTag = prevFilters.filter(filter => filter.id !== "tagIds");
      return value ? [...withoutTag, { id: "tagIds", value }] : withoutTag;
    });
    setPage(0);
    setPageSize(10);
  };

  return (
    <div className="w-full my-2 flex flex-col items-center justify-between gap-2 md:flex-row">
      {!hideInputFilters && (
        <>
          <div className="w-full flex-1">
            <input
              type="text"
              placeholder="Filter By Name"
              className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              defaultValue={debouncedNameFilter}
              onChange={e => debounced("name", e.target.value)}
            />
          </div>
          <div className="w-full flex-1">
            <input
              type="text"
              placeholder="Filter By Owner"
              className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              defaultValue={debouncedOwnerFilter}
              onChange={e => debounced("owner", e.target.value)}
            />
          </div>
          <div className="w-full flex-1">
            <TagValueInput
              entityType={TaggableEntityType.Project}
              value={currentTagFilter}
              onChange={handleTagFilterChange}
            />
          </div>
        </>
      )}
      <span className="w-full flex-1 isolate inline-flex rounded-md shadow-sm">
        <button
          type="button"
          onClick={() => handleSelectPipelineType(ProjectViewType.MyProjects)}
          className={classNames(
            defaultButtonClass,
            leftButtonClass,
            selectedFilterType === ProjectViewType.MyProjects
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          My Projects
        </button>
        <button
          type="button"
          onClick={() => handleSelectPipelineType(ProjectViewType.SharedWithMe)}
          className={classNames(
            defaultButtonClass,
            ability.can("View", "Button")
              ? middleButtonClass
              : rightButtonClass,
            selectedFilterType === ProjectViewType.SharedWithMe
              ? selectedFilterClass
              : unSelectedFilterClass
          )}>
          Shared With Me
        </button>
        <Can I="View" a="Button" ability={ability}>
          <button
            type="button"
            onClick={() => handleSelectPipelineType(ProjectViewType.AdminView)}
            className={classNames(
              defaultButtonClass,
              rightButtonClass,
              selectedFilterType === ProjectViewType.AdminView
                ? selectedFilterClass
                : unSelectedFilterClass
            )}>
            Admin View
          </button>
        </Can>
      </span>
    </div>
  );
};

export default ProjectsFilter;
